import React, { useMemo, useState } from 'react';
import { PlayIcon, UsersIcon } from 'lucide-react';
import { ChatPanel } from './ChatPanel';
import { EntryPanel } from './EntryPanel';
import { EventSettings } from './EventSettings';
import { TileBoard } from './TileBoard';
import { Wheel } from './Wheel';
import { WinnerOverlay } from './WinnerOverlay';
import { useCountdown } from '../hooks/useCountdown';
import { useDraw } from '../hooks/useDraw';
import { browserTimeZone, formatSchedule } from '../utils/schedule';
import type { Session } from '../types/session';
import type { useRoom } from '../hooks/useRoom';
interface HostViewProps {
  session: Session;
  roomApi: ReturnType<typeof useRoom>;
  removeWinners: boolean;
  spinSeconds: number;
  boardMode: 'auto' | 'wheel' | 'tiles';
  onCloseEvent: () => void;
  onRenameSession: (name: string) => void;
}
type Tab = 'names' | 'chat' | 'event';
export function HostView({
  session,
  roomApi,
  removeWinners,
  spinSeconds,
  boardMode,
  onCloseEvent,
  onRenameSession
}: HostViewProps) {
  const [tab, setTab] = useState<Tab>('names');
  const countdown = useCountdown(roomApi.room.startsAt);
  const draw = useDraw({
    removeWinners,
    boardMode,
    onWinner: roomApi.publishWinner,
    onReset: roomApi.clearWinners
  });
  const joinedNames = useMemo(() => roomApi.participants.filter(p => !p.blocked).map(p => p.name), [roomApi.participants]);
  const notInDraw = useMemo(() => {
    const have = new Set(draw.entries.map(e => e.name.toLowerCase()));
    return joinedNames.filter(n => !have.has(n.toLowerCase()));
  }, [draw.entries, joinedNames]);
  const status = !draw.canSpin ? <p className="text-sm text-muted" data-id="element-214" data-mp="1bd9uf8utywib">
      {draw.entries.length === 0 ? 'Add names to get started.' : draw.pool.length === 0 ? 'Everyone has won. Put winners back to run it again.' : 'At least two names are needed to spin.'}
    </p> : draw.history[0] ? <>
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-muted" data-id="element-215" data-mp="1gephf6gkjd3c">
        Last winner
      </p>
      <p className="mt-1.5 font-display text-2xl text-white sm:text-3xl" data-id="element-216" data-mp="dxnctoitmsea">
        {draw.history[0].name}
      </p>
    </> : <p className="text-sm text-muted" data-id="element-217" data-mp="mzmsodi3nqfn">
      {draw.pool.length} names in. Hit spin when the room is ready.
    </p>;
  return <>
      <main className="grid flex-1 grid-cols-1 items-stretch lg:grid-cols-[1fr_21rem] xl:grid-cols-[1fr_24rem]" data-id="element-218" data-mp="rsfxt2hhd0s9">
        <div className="flex min-w-0 flex-col items-center justify-center gap-7 px-5 py-10 sm:px-10 lg:py-12" data-id="element-219" data-mp="ws2c7f91on2n">
          {countdown.total > 0 && roomApi.room.startsAt !== null && <div className="flex w-full max-w-3xl flex-wrap items-center justify-between gap-3 rounded-lg border border-gold/40 bg-surface px-4 py-3 text-left" data-id="element-220" data-mp="15r5uwi4av0gm">
              <div data-id="element-221" data-mp="37ij2jexnjmgd">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-222" data-mp="37javehzv762y">
                  Participants are on a countdown
                </p>
                <p className="mt-1 text-xs text-muted" data-id="element-223" data-mp="34xb93xr9z6yk">
                  Starts{' '}
                  {formatSchedule(roomApi.room.startsAt, roomApi.room.timeZone ?? browserTimeZone())}{' '}
                  · {countdown.days > 0 ? `${countdown.days}d ` : ''}
                  {String(countdown.hours).padStart(2, '0')}:
                  {String(countdown.minutes).padStart(2, '0')}:
                  {String(countdown.seconds).padStart(2, '0')} left
                </p>
              </div>
              <button type="button" onClick={() => roomApi.setSchedule(null, roomApi.room.timeZone ?? browserTimeZone())} className="shrink-0 rounded-lg bg-gold px-3.5 py-2 text-xs font-semibold text-ink transition-[background-color,transform] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold active:scale-[0.97]" data-id="element-224" data-mp="3iy5hwhi8j9xr">
                Start now
              </button>
            </div>}

          {draw.useTiles ? <>
              <div className="w-full" data-id="element-225" data-mp="3chu82jvutpcb">
                <div className="mb-3 flex items-baseline justify-between gap-3" data-id="element-226" data-mp="3heuewx0j2w4h">
                  <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-muted" data-id="element-227" data-mp="3hcrj1wcp28ru">
                    {draw.pool.length} in the draw
                  </p>
                  {draw.spinning && <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-228" data-mp="3w4a3c6cl6ph0">
                      Picking…
                    </p>}
                </div>
                <TileBoard pool={draw.pool} targetIndex={draw.pendingIndex} spinToken={draw.spinToken} spinSeconds={spinSeconds} onSpinComplete={draw.handleSpinComplete} data-id="element-229" data-mp="2kp39i2qp7ojw" />
              </div>
              <button type="button" onClick={draw.spin} disabled={!draw.canSpin || draw.spinning} className="inline-flex items-center gap-2 rounded-full bg-gold px-10 py-4 text-base font-bold uppercase tracking-[0.14em] text-ink transition-[background-color,transform] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-line disabled:text-muted disabled:active:scale-100" data-id="element-230" data-mp="2c7uipxqi4jyn">
                <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-231" data-mp="23ezdv54y4q9e" />
                {draw.spinning ? 'Drawing' : 'Draw a winner'}
              </button>
            </> : <Wheel pool={draw.pool} rotation={draw.rotation} spinning={draw.spinning} spinSeconds={spinSeconds} canSpin={draw.canSpin} onSpin={draw.spin} onSpinComplete={draw.handleSpinComplete} data-id="element-232" data-mp="1aqv3w7fcdr0w" />}

          <div className="flex min-h-12 flex-col items-center text-center" data-id="element-233" data-mp="296sspcarqaho">{status}</div>
        </div>

        <div className="flex flex-col overflow-hidden border-t border-line px-5 py-5 sm:px-7 lg:h-[calc(100vh-7.5rem)] lg:border-l lg:border-t-0" data-id="element-234" data-mp="2e6xcpcavxncn">
          <div className="flex gap-5 border-b border-line" role="tablist" data-id="element-235" data-mp="1hhxyzhrj7td2">
            {(['names', 'chat', 'event'] as Tab[]).map(t => <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className={`-mb-px border-b-2 pb-2.5 text-sm font-semibold transition-colors duration-150 ease-emphasis focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold ${tab === t ? 'border-gold text-white' : 'border-transparent text-muted hover:text-body'}`} data-id="element-236" data-mp="1qg9zqcm447j8">
                {t === 'names' ? 'Names' : t === 'event' ? 'Event' : `Chat${roomApi.messages.length ? ` · ${roomApi.messages.length}` : ''}`}
              </button>)}
          </div>

          <div className="mt-4 flex min-h-0 flex-1 flex-col" data-id="element-237" data-mp="chzqw3pdh8iu">
            {tab === 'names' ? <>
                {notInDraw.length > 0 && <button type="button" onClick={() => draw.addNames(notInDraw)} className="mb-4 inline-flex items-center gap-2 rounded-lg border border-gold/50 px-3 py-2 text-xs font-semibold text-gold transition-colors duration-150 ease-emphasis hover:bg-gold hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-238" data-mp="2isiluxhw42l">
                    <UsersIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-239" data-mp="10uphlqvcrmon" />
                    Add {notInDraw.length} joined participant
                    {notInDraw.length === 1 ? '' : 's'}
                  </button>}
                <EntryPanel entries={draw.entries} activeCount={draw.pool.length} drawnCount={draw.drawnCount} history={draw.history} disabled={draw.spinning} removeWinners={removeWinners} onAdd={draw.addNames} onRemove={draw.removeEntry} onResetWinners={draw.resetWinners} onClearAll={draw.clearAll} data-id="element-240" data-mp="w1e7ngv8upsi" />
              </> : tab === 'event' ? <EventSettings roomCode={session.roomCode ?? ''} eventName={roomApi.room.name} chatEnabled={roomApi.room.chatEnabled} closed={roomApi.room.closed} joinedCount={roomApi.participants.filter(p => !p.blocked).length} winnersCount={roomApi.winners.length} startsAt={roomApi.room.startsAt} timeZone={roomApi.room.timeZone} onSetSchedule={roomApi.setSchedule} onRename={next => {
            roomApi.renameEvent(next);
            onRenameSession(next);
          }} onSetChatEnabled={roomApi.setChatEnabled} onSetClosed={roomApi.setClosed} onClearWinners={draw.resetWinners} onCloseEvent={onCloseEvent} data-id="element-241" data-mp="23g96bdr587zw" /> : <ChatPanel session={session} room={roomApi.room} messages={roomApi.messages} participants={roomApi.participants} me={roomApi.me} onSend={roomApi.sendMessage} onDeleteMessage={roomApi.deleteMessage} onWipeChat={roomApi.wipeChat} onSetChatEnabled={roomApi.setChatEnabled} onSetMuted={roomApi.setMuted} onSetBlocked={roomApi.setBlocked} data-id="element-242" data-mp="jjtplg86immt" />}
          </div>
        </div>
      </main>

      <WinnerOverlay winner={draw.winner} remainingAfter={removeWinners ? draw.pool.length - 1 : draw.pool.length} removeWinners={removeWinners} onClose={draw.commitWinner} onSpinAgain={draw.spinAgain} data-id="element-243" data-mp="72mjc5bn7e9x" />
    </>;
}